import {
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
  Firestore,
} from "firebase/firestore";
import { shouldForceHideFromWorkflowStatus } from "@/utils/workflowForceHide";

// KB-3308: the live-only publish/unpublish path (behind VITE_LOCATION_EDIT_LIVE_SOURCE).
//
// These functions are PURE service functions: they take a Firestore instance and touch only
// the `campLocations` doc. No toast, no `window`, no `document`, no version machinery — so the
// KB-2678 revert emulator test can call `publishLocationLiveOnly` directly and observe that a
// variation written only to the live doc SURVIVES a publish (the whole point of the flag).
//
// Publish/unpublish flip visibility only: they write `visibility`, `publishedAt` and the derived
// `forceHide`, plus the `lastEditedBy`/`lastEditedAt` stamp in the SAME write (KB-3471 ruling 1 —
// the attribution trigger resolves the actor from the write that changed the doc, so a separate
// stamp write credits the previous editor). They never spread a `campLocationVersions` snapshot
// over the live doc, which is what silently reverted live-only variation writes on the old path.

/**
 * Publish a location by flipping visibility only. Writes `visibility: "v_published"`,
 * `publishedAt`, the `forceHide` derived from the location's own workflow status, and the
 * editor stamp — all in one write. Does not require any version to exist and does not touch
 * `campLocationVersions`.
 *
 * `forceHide` is derived from the location's current `workflowStatus` so publish never sets
 * `forceHide: false` on a location its workflow status hides.
 *
 * @returns the location id on success (truthy — the welcome callers treat a falsy return as a
 *   failure and would show a false "Publish failed" toast).
 */
export const publishLocationLiveOnly = async (
  db: Firestore,
  locationId: string,
  userId: string,
): Promise<string> => {
  const locRef = doc(db, "campLocations", locationId);
  const snapshot = await getDoc(locRef);
  if (!snapshot.exists()) {
    throw new Error(`Location ${locationId} not found`);
  }

  const workflowStatus = (snapshot.data() as { workflowStatus?: string | null }).workflowStatus ?? null;
  const userRef = userId !== "system" ? doc(db, "users", userId) : null;

  await updateDoc(locRef, {
    visibility: "v_published",
    publishedAt: serverTimestamp(),
    forceHide: shouldForceHideFromWorkflowStatus(workflowStatus),
    lastEditedBy: userRef,
    lastEditedAt: serverTimestamp(),
  });

  return locationId;
};

/**
 * Unpublish a location by flipping visibility only. Writes `visibility: "v_unpublished"`,
 * `forceHide: true`, and the editor stamp in one write. Does not touch `campLocationVersions`.
 *
 * @returns the location id on success.
 */
export const unpublishLocationLiveOnly = async (
  db: Firestore,
  locationId: string,
  userId: string,
): Promise<string> => {
  const locRef = doc(db, "campLocations", locationId);
  const snapshot = await getDoc(locRef);
  if (!snapshot.exists()) {
    throw new Error(`Location ${locationId} not found`);
  }

  const userRef = userId !== "system" ? doc(db, "users", userId) : null;

  await updateDoc(locRef, {
    visibility: "v_unpublished",
    forceHide: true,
    lastEditedBy: userRef,
    lastEditedAt: serverTimestamp(),
  });

  return locationId;
};
